import type { ReactNode } from 'react';
import StaticSummary from './StaticSummary';

export interface StaticSummaryGroupItem {
    title: ReactNode;
    description: ReactNode;
}

export interface StaticSummaryGroupProps {
    items: StaticSummaryGroupItem[];
    /**
     * Row formation, e.g. [3, 2] renders first row with up to 3, second with up to 2.
     * Extra items beyond the sum will be placed in an additional row.
     */
    formation: number[];
    className?: string;
    itemClassName?: string;
    /**
     * When true, ignore formation and render each item as a single full-width row.
     */
    isMobile?: boolean;
}

const buildRows = (items: StaticSummaryGroupItem[], formation: number[]): StaticSummaryGroupItem[][] => {
    const rows: StaticSummaryGroupItem[][] = [];
    let index = 0;
    for (let i = 0; i < formation.length && index < items.length; i += 1) {
        const count = Math.max(1, formation[i]);
        const slice = items.slice(index, index + count);
        if (slice.length > 0) {
            rows.push(slice);
        }
        index += count;
    }
    if (index < items.length) {
        rows.push(items.slice(index));
    }
    return rows;
};

const StaticSummaryGroup = ({ items, formation, className, itemClassName, isMobile }: StaticSummaryGroupProps) => {
    const rows = isMobile ? items.map(i => [i]) : buildRows(items, formation);

    return (
        <div className={`w-full ${className || ''}`}>
            {rows.map((row, rowIdx) => {
                const plannedCols = isMobile ? row.length : (formation[rowIdx] ?? row.length);
                const cols = Math.max(1, Math.min(plannedCols, row.length));
                return (
                    <div
                        key={rowIdx}
                        className={rowIdx > 0 ? 'mt-5' : ''}
                        style={{
                            display: 'grid',
                            gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
                            gap: '20px',
                        }}
                    >
                        {row.map((item, idx) => (
                            <StaticSummary
                                key={idx}
                                title={item.title}
                                description={item.description}
                                className={`h-full ${itemClassName || ''}`}
                            />
                        ))}
                    </div>
                );
            })}
        </div>
    );
};

export default StaticSummaryGroup;
